import React, { useState, useEffect } from 'react';
import { Button, Form, Spinner, Alert, Row, Col, Card, ProgressBar, InputGroup, FormControl } from 'react-bootstrap';
import DataTable from "react-data-table-component"; // We will use this to display data in a table format
import { CSVLink } from "react-csv";
import { ChargingBASE_URL } from '../../Config';
import { BASE_URL } from '../../Config';

const UploadFileForReversalTXT = () => {
    // State variables
    const [file, setFile] = useState(null);
    const [userId, setUserId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [responseData, setResponseData] = useState(null);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortDirection, setSortDirection] = useState("asc");
    const [isReversalLoading, setIsReversalLoading] = useState(false);

    useEffect(() => {
        // Fetch UserId from session storage
        const user = JSON.parse(sessionStorage.getItem('user'));
        if (user && user.userId) {
            setUserId(user.userId);
        } else {
            setError("User ID not found. Please log in.");
        }
    }, []);

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleUpload = async () => {
        if (!file) {
            setError("Please select a text file to upload.");
            return;
        }

        if (!userId) {
            setError("User ID is missing. Please log in.");
            return;
        }

        const formData = new FormData();
        formData.append("TextFile", file); // Upload the selected text file
        formData.append("UserId", userId); // Include the UserId

        const url = `${ChargingBASE_URL}UploadFile_Reversaltxt`;

        try {
            setIsLoading(true);
            setError(null);
            setProgress(10); // Set initial progress

            const response = await fetch(url, {
                method: 'POST',
                body: formData, // Send the form data
            });

            if (response.ok) {
                const data = await response.json();
                setResponseData(data);

                // Simulate progress update
                const progressInterval = setInterval(() => {
                    setProgress((prevProgress) => {
                        if (prevProgress >= 100) {
                            clearInterval(progressInterval);
                            return 100;
                        }
                        return prevProgress + 10;
                    });
                }, 200);
            } else {
                throw new Error('Failed to upload the file.');
            }
        } catch (error) {
            setError(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleReversal = async (transactionId, id) => {
        if (!userId || !transactionId || !id) return;

        setIsReversalLoading(true); // Set the loading state to true when the reversal starts

        const url = `${BASE_URL}JazzCash_Reversal`;
        const requestData = {
            userId: userId,
            originalTransactionId: transactionId,
            id: id,
        };

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestData),
            });

            if (response.ok) {
                const data = await response.json();
                alert(data.message);
            } else {
                throw new Error("Reversal request failed");
            }
        } catch (error) {
            alert(`Error: ${error.message}`);
        } finally {
            setIsReversalLoading(false); // Set the loading state to false once the request completes
        }
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const filteredData = responseData
        ? responseData.response.filter((item) => {
            return (
                item.agentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.transactionId.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.status.toLowerCase().includes(searchQuery.toLowerCase())
            );
        })
        : [];

    const columns = [
        { name: "Id", selector: (row) => row.id, sortable: true },
        { name: "Agent Name", selector: (row) => row.agentName, sortable: true },
        { name: "Company Name", selector: (row) => row.companyName, sortable: true },
        { name: "Timestamp", selector: (row) => row.timestamp, sortable: true },
        { name: "Reason", selector: (row) => row.reason, sortable: true },
        { name: "Amount", selector: (row) => row.amount, sortable: true },
        { name: "Account Number", selector: (row) => row.accountNumber, sortable: true },
        { name: "Status", selector: (row) => row.status, sortable: true },
        { name: "Transaction ID", selector: (row) => row.transactionId, sortable: true },
        {
            name: "Action",
            cell: (row) => {
                if (row.status === "Sub") {
                    return (
                        <Button
                            variant="danger"
                            onClick={() => handleReversal(row.transactionId, row.id)}
                            disabled={isReversalLoading} // Disable the button when reversal is loading
                        >
                            {isReversalLoading ? <Spinner animation="border" size="sm" /> : "Reversal"}
                        </Button>
                    );
                }
                return null;
            },
            sortable: false,
        },
    ];

    const handleSort = (column, sortDirection) => {
        setSortDirection(sortDirection);
        const sortedData = [...filteredData].sort((a, b) => {
            if (sortDirection === "asc") {
                return a[column.selector] > b[column.selector] ? 1 : -1;
            } else {
                return a[column.selector] < b[column.selector] ? 1 : -1;
            }
        });
        setResponseData((prevState) => ({ ...prevState, response: sortedData }));
    };

    return (
        <div className="container mt-1">
            <Row className="justify-content-center">
                <Col lg={12} md={12} sm={12}>
                    <Card className="shadow-lg">
                        <Card.Body>
                            {/* File Upload Form */}
                            <Form >
                                <Row className="mb-2">
                                    <Col md={8}>
                                        <Form.Group controlId="fileUpload">
                                            <Form.Control
                                                type="file"
                                                accept=".txt"
                                                onChange={handleFileChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} sm={12} md={4} lg={4}>
                                        {/* Upload Button */}
                                        <Button
                                            variant="primary"
                                            onClick={handleUpload}
                                            className="w-100"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? 'Uploading...' : 'Upload File'}
                                        </Button>
                                    </Col>

                                    {/* Display Loading Progress */}
                                    {isLoading && (
                                        <div className="text-center mt-3">
                                            <ProgressBar animated now={progress} label={`Uploading... ${progress} %`} />
                                        </div>
                                    )}

                                    {/* Error Message */}
                                    {error && (
                                        <Alert variant="danger" className="mt-3">
                                            {error}
                                        </Alert>
                                    )}
                                </Row>
                            </Form>

                            {/* Displaying Response Data */}
                            {responseData && responseData.responseCode === "0000" && (
                                <Card className="shadow-lg">
                                    <Card.Header>
                                        <Row>
                                            <Col lg={8} md={10} sm={12}>
                                                <h6 className="text-center">Processed Transactions</h6>
                                            </Col>
                                            <Col lg={4} md={2} sm={12}>
                                                <InputGroup>
                                                    <FormControl
                                                        placeholder="Search....."
                                                        value={searchQuery}
                                                        onChange={handleSearchChange}
                                                    />
                                                </InputGroup>
                                            </Col>
                                        </Row>
                                    </Card.Header>
                                    <Card.Body>
                                        <DataTable
                                            columns={columns}
                                            data={filteredData}
                                            pagination
                                            paginationPerPage={10}
                                            paginationRowsPerPageOptions={[5, 10, 15, 20]}
                                            highlightOnHover
                                            sortServer
                                            sortDirection={sortDirection}
                                            onSort={handleSort}
                                            persistTableHead
                                        />

                                        <div className="text-center mt-3">
                                            <CSVLink
                                                data={responseData.response.map((item) => ({
                                                    Id: item.id,
                                                    AgentName: item.agentName,
                                                    CompanyName: item.companyName,
                                                    Timestamp: item.timestamp,
                                                    Reason: item.reason,
                                                    Amount: item.amount,
                                                    AccountNumber: item.accountNumber,
                                                    Status: item.status,
                                                    TransactionId: item.transactionId,
                                                }))}
                                                filename={"ReversalData.csv"}
                                            >
                                                <Button variant="success" className="w-100">
                                                    Download CSV
                                                </Button>
                                            </CSVLink>
                                        </div>
                                    </Card.Body>
                                </Card>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default UploadFileForReversalTXT;
